/**
 * Task Estimation Validation Service - Phase 4 Architecture
 *
 * Encapsulates estimation validation and actual duration recording.
 * Extracted from TaskEstimationService to reduce single-responsibility violations.
 */

import { typedSupabase } from '@/lib/typed-supabase';
import { logger } from '@/lib/logger';
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from '@/services/core/service-registry';
import type {
  TaskEstimation,
  UpdateTaskEstimationRequest,
  ValidationResult,
} from "@/types/time-management";
import { TaskEstimationCrudService } from './task-estimation-crud.service';

export class TaskEstimationValidationService implements IService {
  public readonly name = "TaskEstimationValidationService";
  private readonly supabase = typedSupabase;
  private crudService: TaskEstimationCrudService;

  constructor(crudService: TaskEstimationCrudService) {
    this.crudService = crudService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ TaskEstimationValidationService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskEstimationValidationService disposed");
  }

  async validateEstimation(
    estimation: TaskEstimation
  ): Promise<ServiceResponse<ValidationResult>> {
    try {
      const issues: string[] = [];
      const warnings: string[] = [];

      if (estimation.estimated_duration < 5) {
        warnings.push("Estimated duration is very short (< 5 minutes)");
      }
      if (estimation.estimated_duration > 480) {
        warnings.push("Estimated duration is very long (> 8 hours)");
      }

      if (estimation.factors && estimation.factors.length > 0) {
        const totalWeight = estimation.factors.reduce(
          (sum, factor) => sum + factor.weight,
          0
        );
        if (Math.abs(totalWeight - 1.0) > 0.1) {
          warnings.push("Factor weights don't sum to 1.0");
        }
      }

      if (
        estimation.estimation_method === "manual" &&
        estimation.confidence_level === "high"
      ) {
        warnings.push("High confidence unusual for manual estimation");
      }

      const isValid = issues.length === 0;

      return createSuccessResponse({
        isValid,
        errors: issues,
        warnings,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async recordActualDuration(
    taskId: string,
    actualMinutes: number
  ): Promise<
    ServiceResponse<{
      accuracy: number;
      variance: number;
      estimationId: string;
    }>
  > {
    try {
      const { data: estimation, error } = await this.supabase
        .from("task_estimations")
        .select("*")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error) {
        return createErrorResponse("No estimation found for this task");
      }

      const updateResult = await this.crudService.updateEstimation(estimation.id, {
        actual_duration: actualMinutes,
      } as UpdateTaskEstimationRequest);

      if (updateResult.error) {
        return createErrorResponse(updateResult.error);
      }

      const accuracy = this.calculateAccuracy(
        estimation.estimated_duration,
        actualMinutes
      );
      const variance = Math.abs(estimation.estimated_duration - actualMinutes);

      return createSuccessResponse({
        accuracy,
        variance,
        estimationId: estimation.id,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  private calculateAccuracy(estimated: number, actual: number): number {
    if (estimated === 0 || actual === 0) {return 0;}
    const ratio = Math.min(estimated, actual) / Math.max(estimated, actual);
    return Math.round(ratio * 100) / 100;
  }
}
